import React, { createContext, useContext, useState, useCallback } from 'react';
import apiClient from '../services/unifiedApiClient';

const defaultSettings = {
  themeMode: 'system',
  customSettings: {},
  notifications: true,
  trelloIntegration: {
    enabled: false,
    apiKey: '',
    token: ''
  },
  jiraIntegration: {
    enabled: false,
    jiraUrl: '',
    userEmail: '',
    apiToken: '',
    accountId: ''
  }
};

const SettingsContext = createContext({
  settings: defaultSettings,
  updateSettings: () => {},
  loadUserSettings: () => {},
  settingsLoaded: false,
});

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(defaultSettings);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Load user-specific settings from backend
  const loadUserSettings = useCallback(async (userId) => {
    console.log('[SettingsContext] loadUserSettings called with userId:', userId);

    if (!userId) {
      // No user - use default settings
      console.log('[SettingsContext] No user, resetting to default settings');
      setSettings(defaultSettings);
      setCurrentUserId(null);
      setSettingsLoaded(false);
      return;
    }

    if (userId === currentUserId && settingsLoaded) {
      // Same user and settings already loaded, no need to reload
      console.log('[SettingsContext] Same user and settings loaded, no reload needed');
      return;
    }

    try {
      // Fetch settings from backend API
      console.log('[SettingsContext] Fetching settings from API for user:', userId);
      const response = await apiClient.get('/settings');
      console.log('[SettingsContext] API response:', response);

      // apiClient.get() already unwraps response.data, so response IS the data directly
      // The backend returns { data: {...settings} }, so extract from response.data
      const backendSettings = response?.data;
      console.log('[SettingsContext] Backend settings:', backendSettings);

      if (backendSettings && backendSettings.user_id) {
        console.log('[SettingsContext] Settings loaded from backend:', backendSettings);

        // Transform backend format to frontend format
        const transformedSettings = {
          themeMode: backendSettings.custom_settings?.theme_mode || (backendSettings.dark_mode === true ? 'dark' : 'system'),
          customSettings: backendSettings.custom_settings || {},
          notifications: backendSettings.notifications !== undefined ? backendSettings.notifications : true,
          trelloIntegration: {
            enabled: backendSettings.trello_integration?.enabled || false,
            apiKey: backendSettings.trello_integration?.api_key || '',
            token: backendSettings.trello_integration?.token || ''
          },
          jiraIntegration: {
            enabled: backendSettings.jira_integration?.enabled || false,
            jiraUrl: backendSettings.jira_integration?.jira_url || '',
            userEmail: backendSettings.jira_integration?.user_email || '',
            apiToken: backendSettings.jira_integration?.api_token || '',
            accountId: backendSettings.jira_integration?.account_id || ''
          }
        };

        setSettings(transformedSettings);
        setCurrentUserId(userId);
        setSettingsLoaded(true);
      } else {
        // No settings in backend, use defaults
        console.log('[SettingsContext] No settings in backend, using defaults');
        setSettings(defaultSettings);
        setCurrentUserId(userId);
        setSettingsLoaded(true);
      }
    } catch (error) {
      console.error('[SettingsContext] Error loading settings from backend:', error);

      // Fallback to localStorage
      const userSettingsKey = `userSettings:${userId}`;
      const saved = localStorage.getItem(userSettingsKey);

      if (saved) {
        try {
          const parsedSettings = JSON.parse(saved);
          const mergedSettings = {
            ...defaultSettings,
            ...parsedSettings,
            trelloIntegration: {
              ...defaultSettings.trelloIntegration,
              ...parsedSettings.trelloIntegration
            },
            jiraIntegration: {
              ...defaultSettings.jiraIntegration,
              ...parsedSettings.jiraIntegration
            }
          };
          setSettings(mergedSettings);
        } catch (parseError) {
          console.error('[SettingsContext] Error parsing localStorage settings:', parseError);
          setSettings(defaultSettings);
        }
      } else {
        setSettings(defaultSettings);
      }

      setCurrentUserId(userId);
      setSettingsLoaded(true);
    }
  }, [currentUserId, settingsLoaded]);

  const updateSettings = useCallback((newSettings) => {
    console.log('[SettingsContext] updateSettings called with:', newSettings);
    setSettings(newSettings);

    // Save directly to backend when user updates settings
    if (!currentUserId) {
      console.log('[SettingsContext] No user, skipping save');
      return;
    }

    // Transform frontend format to backend format
    const backendSettings = {
      dark_mode: newSettings.themeMode === 'dark' ? true : false,
      notifications: newSettings.notifications,
      trello_integration: {
        enabled: newSettings.trelloIntegration.enabled,
        api_key: newSettings.trelloIntegration.apiKey,
        token: newSettings.trelloIntegration.token
      },
      jira_integration: {
        enabled: newSettings.jiraIntegration.enabled,
        jira_url: newSettings.jiraIntegration.jiraUrl,
        user_email: newSettings.jiraIntegration.userEmail,
        api_token: newSettings.jiraIntegration.apiToken,
        account_id: newSettings.jiraIntegration.accountId
      },
      custom_settings: {
        ...(newSettings.customSettings || {}),
        theme_mode: newSettings.themeMode,
      }
    };

    console.log('[SettingsContext] Saving settings to backend:', backendSettings);

    // Save to backend API
    apiClient.put('/settings', backendSettings)
      .then(() => {
        console.log('[SettingsContext] Settings saved to backend successfully');

        // Also save to localStorage as backup
        const userSettingsKey = `userSettings:${currentUserId}`;
        localStorage.setItem(userSettingsKey, JSON.stringify(newSettings));
      })
      .catch((error) => {
        console.error('[SettingsContext] Failed to save settings to backend:', error);

        // Fallback: save to localStorage only
        const userSettingsKey = `userSettings:${currentUserId}`;
        localStorage.setItem(userSettingsKey, JSON.stringify(newSettings));
      });
  }, [currentUserId]);

  return (
    <SettingsContext.Provider value={{ settings, updateSettings, loadUserSettings, settingsLoaded }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
